<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <v-card v-cloak>
      <v-card-title class="d-flex justify-space-between">
        {{ $t('console.manageGroups') }}

        <v-btn icon @click="dialog = false;">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text scrollable class="scrollable mt-4">
        <v-form ref="form">
          <div v-for="group in getGroups" :key="group.id" class="mb-4">
            <div class="d-flex">
              <v-text-field
                v-model="groupNamesInputs[group.id]"
                :label="$t('console.groupName')"
                :placeholder="$t('console.groupName')"
                :rules="groupNameInputRules"
                :disabled="isMainGroup(group)"
                variant="solo-filled"
                hide-details="auto"
                class="flex-grow-1"
                flat
              />

              <v-btn v-if="!isMainGroup(group)" icon @click="removeGroup(group.id)" elevation='0'
                     class="ml-2">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>


            <v-row>
              <v-col cols="12" sm="6" offset-sm=3>
                <v-list-subheader v-if="hasAnyFilesAndFoldersPermissions">
                  <h3>{{ $t('menus.userPermissions.nodesAndPods') }}</h3>
                </v-list-subheader>

                <div class="d-flex align-center" v-if="hasParentPermission('list', 'NodeAndPod')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="nodeAndPodList[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.listNodesAndPods')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  ></v-switch>
                </div>

                <div class="d-flex align-center"
                     v-if="hasParentPermission('control', 'NodeAndPod')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="nodeAndPodControl[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.controlNodesAndPods')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  ></v-switch>
                </div>

                <v-switch
                  v-if="hasParentPermission('delete', 'NodeAndPod')"
                  style="margin-top: 0;"
                  v-model="nodeAndPodDelete[group.id]"
                  :label="$t('menus.userPermissions.deleteNodesAndPods')"
                  :disabled="isMainGroup(group)"
                  hide-details
                  color="primary"
                  density="compact"
                  block
                >
                </v-switch>

                <div class="d-flex align-center"
                     v-if="hasParentPermission('download', 'NodeAndPod')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="nodeAndPodDownload[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.downloadNodesAndPods')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  ></v-switch>
                </div>

                <div class="d-flex align-center" v-if="hasParentPermission('list', 'Asset')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="assetList[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.showPodAssets')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  ></v-switch>
                </div>

                <div class="d-flex align-center"
                     v-if="hasParentPermission('upload', 'FileAndAsset')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="fileAndAssetUpload[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.uploadFilesAndAssets')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  ></v-switch>
                </div>

                <div class="d-flex align-center" v-if="hasParentPermission('list', 'ShareLinks')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="listShareLinks[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.listShareLinks')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  ></v-switch>
                </div>

                <div class="d-flex align-center"
                     v-if="hasParentPermission('control', 'ShareLinks')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="controlShareLinks[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.controlShareLinks')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  ></v-switch>
                </div>

                <div v-if="$canArchiveNodeAndPod() && hasParentPermission('archive', 'NodeAndPod')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="nodeAndPodArchive[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.archiveNodesAndPods')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  >
                  </v-switch>
                </div>

                <div class="d-flex align-center"
                     v-if="hasParentPermission('viewAnalytics', 'NodeAndPod')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="viewAnalytics[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.viewAnalytics')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  ></v-switch>
                </div>

                <div class="d-flex align-center" v-if="hasParentPermission('label', 'Pod')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="podsLabel[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.labelPods')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                    block
                  ></v-switch>
                </div>
              </v-col>
              <v-col cols="12" sm="6" offset-sm=3>
                <v-list-subheader v-if="hasAnyServicesPermissions">
                  <h3>{{ $t('menus.userPermissions.services') }}</h3>
                </v-list-subheader>

                <div class="d-flex align-center" v-if="hasParentPermission('list', 'PodService')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="podServiceList[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.listPodServices')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                  ></v-switch>
                </div>

                <div class="d-flex align-center"
                     v-if="hasParentPermission('control', 'PodService')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="podServiceControl[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.controlPodServices')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                  ></v-switch>
                </div>

                <div class="d-flex align-center" v-if="hasParentPermission('run', 'FreeService')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="useFreeServices[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.useFreeServices')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                  ></v-switch>
                </div>

                <div class="d-flex align-center" v-if="hasParentPermission('run', 'PaidService')">
                  <v-switch
                    style="margin-top: 0;"
                    v-model="usePaidServices[group.id]"
                    color="primary"
                    :label="$t('menus.userPermissions.usePaidServices')"
                    :disabled="isMainGroup(group)"
                    density="compact"
                    hide-details
                  ></v-switch>
                </div>
              </v-col>
            </v-row>

            <hr class="my-4"/>
          </div>

          <div v-if="errorMessage" ref="errorMessage" class="bg-error my-2" data-cy="-----">
            {{ errorMessage }}
          </div>

          <div class="d-flex justify-center">
            <v-btn color="primary" @click="addGroup" elevation="0">
              {{ $t('console.addGroup') }}
            </v-btn>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn type="submit" class="bg-primary" elevation=0 v-bind:loading="isSubmitted"
               v-bind:disabled="isSubmitted" @click="submitForm">
          {{ $t('common.save') }}
        </v-btn>
        <v-btn variant="text" @click="dialog = false">{{ $t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useTenantConnectionsStore } from '@/stores/TenantConnectionsStore.js';

export default {
  mixins: [ DialogActionKeys, DialogModel ],
  name: 'AwayTenantGroupsDialog',
  props: {
    tenantConnection: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isSubmitted: false,

      errorMessage: '',
      successMessage: '',

      newGroups: [],
      deletedGroupIds: [],

      groupNamesInputs: {},
      groupNameInputRules: [
        v => !!v || this.$t('console.groupNameRequired'),
      ],

      // Permissions - Initialized from prop or defaults
      nodeAndPodList: {},
      nodeAndPodControl: {},
      nodeAndPodDelete: {},
      nodeAndPodDownload: {},
      assetList: {},
      fileAndAssetUpload: {},
      listShareLinks: {},
      controlShareLinks: {},
      nodeAndPodArchive: {},
      viewAnalytics: {},
      podsLabel: {},

      podServiceList: {},
      podServiceControl: {},
      useFreeServices: {},
      usePaidServices: {},
    };
  },

  created() {
    this.resetForm();
  },

  watch: {
    dialog() {
      if (this.dialog) {
        this.resetForm();
      }
    },

    tenantConnection: {
      handler() {
        this.resetForm();
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    resetForm() {
      this.isSubmitted = false;
      this.errorMessage = '';
      this.successMessage = '';

      this.groupNamesInputs = {};
      this.newGroups = [];
      this.deletedGroupIds = [];

      // Permissions - Initialized from prop or defaults
      this.nodeAndPodList = {};
      this.nodeAndPodControl = {};
      this.nodeAndPodDelete = {};
      this.nodeAndPodDownload = {};
      this.assetList = {};
      this.fileAndAssetUpload = {};
      this.listShareLinks = {};
      this.controlShareLinks = {};
      this.nodeAndPodArchive = {};
      this.viewAnalytics = {};
      this.podsLabel = {};

      this.podServiceList = {};
      this.podServiceControl = {};
      this.useFreeServices = {};
      this.usePaidServices = {};

      for (const group of this.getGroups) {
        this.groupNamesInputs[group.id] = group.name;

        const abilitiesList = group.abilities || [];

        this.nodeAndPodList[group.id] = this.findAbility(abilitiesList, 'list', 'NodeAndPod');
        this.nodeAndPodControl[group.id] = this.findAbility(abilitiesList, 'control', 'NodeAndPod');
        this.nodeAndPodDelete[group.id] = this.findAbility(abilitiesList, 'delete', 'NodeAndPod');
        this.nodeAndPodDownload[group.id] = this.findAbility(abilitiesList, 'download', 'NodeAndPod');
        this.assetList[group.id] = this.findAbility(abilitiesList, 'list', 'Asset');
        this.fileAndAssetUpload[group.id] = this.findAbility(abilitiesList, 'upload', 'FileAndAsset');
        this.listShareLinks[group.id] = this.findAbility(abilitiesList, 'list', 'ShareLinks');
        this.controlShareLinks[group.id] = this.findAbility(abilitiesList, 'control', 'ShareLinks');
        this.nodeAndPodArchive[group.id] = this.findAbility(abilitiesList, 'archive', 'NodeAndPod');
        this.viewAnalytics[group.id] = this.findAbility(abilitiesList, 'viewAnalytics', 'NodeAndPod');
        this.podsLabel[group.id] = this.findAbility(abilitiesList, 'label', 'Pod');

        this.podServiceList[group.id] = this.findAbility(abilitiesList, 'list', 'PodService');
        this.podServiceControl[group.id] = this.findAbility(abilitiesList, 'control', 'PodService');
        this.useFreeServices[group.id] = this.findAbility(abilitiesList, 'run', 'FreeService');
        this.usePaidServices[group.id] = this.findAbility(abilitiesList, 'run', 'PaidService');
      }
    },
    findAbility(abilities, action, subject) {
      for (let i = 0; i < abilities.length; i++) {
        if (abilities[i].action === action && abilities[i].subject === subject) {
          return true;
        }
      }
      return false;
    },
    addGroup() {
      this.newGroups.push({
        id: uuidv4(),
        name: '',
        abilities: [],
        isNewGroup: true,
      });
    },
    removeGroup(id) {
      const index = this.newGroups.findIndex(group => group.id === id);
      if (index >= 0) {
        this.newGroups.splice(index, 1);
      } else {
        this.deletedGroupIds.push(id);
      }
    },
    async submitForm() {
      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        return;
      }

      const formData = [];

      for (const group of this.getGroups) {
        if (group.id === this.getParentGroup?.id) {
          continue;
        }

        formData.push({
          id: group.isNewGroup ? null : group.id,
          name: this.groupNamesInputs[group.id],
          permissions: [
            this.nodeAndPodList[group.id] && 'NodeAndPod.list',
            this.nodeAndPodControl[group.id] && 'NodeAndPod.control',
            this.nodeAndPodDelete[group.id] && 'NodeAndPod.delete',
            this.nodeAndPodDownload[group.id] && 'NodeAndPod.download',
            this.assetList[group.id] && 'Asset.list',
            this.fileAndAssetUpload[group.id] && 'FileAndAsset.upload',
            this.listShareLinks[group.id] && 'ShareLinks.list',
            this.controlShareLinks[group.id] && 'ShareLinks.control',
            this.nodeAndPodArchive[group.id] && 'NodeAndPod.archive',
            this.viewAnalytics[group.id] && 'NodeAndPod.viewAnalytics',
            this.podsLabel[group.id] && 'Pod.label',
            this.podServiceList[group.id] && 'PodService.list',
            this.podServiceControl[group.id] && 'PodService.control',
            this.useFreeServices[group.id] && 'FreeService.run',
            this.usePaidServices[group.id] && 'PaidService.run',
          ].filter(Boolean),
        });
      }

      try {
        this.isSubmitted = true;

        const connectionId = this.tenantConnection?.id;
        const tenantConnectionsStore = useTenantConnectionsStore();
        await tenantConnectionsStore.awayTenantSaveGroups(this.rootNodeId, connectionId, formData);

        this.dialog = false;
        this.$showMessage(this.$t('console.connectionGroupsUpdatedSuccessfully'), { type: 'success' });

        await tenantConnectionsStore.refreshAwayTenantsList(this.rootNodeId);
      } catch (error) {
        console.error(error);

        if (error?.response?.data?.error) {
          this.errorMessage = this.$localizeMessage(error.response.data.error);
        } else {
          this.errorMessage = this.$t('backend.system-error');
        }

        this.$nextTick(() => {
          if (this.$refs.errorMessage && typeof this.$refs.errorMessage.scrollIntoView === 'function') {
            this.$refs.errorMessage.scrollIntoView({
              behavior: 'smooth',
            });
          }
        });
      } finally {
        this.isSubmitted = false;
      }
    },
  },
  computed: {
    rootNodeId: function () {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    getParentGroup() {
      if (!this.tenantConnection ||
        !this.tenantConnection.groups ||
        this.tenantConnection.groups.length === 0) {
        return null;
      }

      return this.tenantConnection.groups.find(x => x.parentid === null);
    },
    getChildGroups() {
      if (!this.tenantConnection ||
        !this.tenantConnection.groups ||
        this.tenantConnection.groups.length === 0) {
        return [];
      }

      return this.tenantConnection.groups.filter(x => x.parentid !== null);
    },
    getGroups() {
      if (!this.getParentGroup) {
        return [];
      }

      return [
        this.getParentGroup,
        ...this.getChildGroups,
        ...this.newGroups,
      ]
        .filter(Boolean)
        .filter(x => this.deletedGroupIds.indexOf(x.id) < 0);
    },
    isMainGroup() {
      return function (group) {
        return this.getParentGroup && this.getParentGroup.id === group.id;
      };
    },
    parentGroupAbilities() {
      return this.getParentGroup ? (this.getParentGroup.abilities || []) : [];
    },
    hasParentPermission() {
      return (action, subject) => {
        return this.findAbility(this.parentGroupAbilities, action, subject);
      };
    },
    hasAnyFilesAndFoldersPermissions() {
      return this.hasParentPermission('list', 'NodeAndPod') ||
        this.hasParentPermission('control', 'NodeAndPod') ||
        this.hasParentPermission('delete', 'NodeAndPod') ||
        this.hasParentPermission('download', 'NodeAndPod') ||
        this.hasParentPermission('list', 'Asset') ||
        this.hasParentPermission('upload', 'FileAndAsset') ||
        this.hasParentPermission('list', 'ShareLinks') ||
        this.hasParentPermission('control', 'ShareLinks') ||
        this.hasParentPermission('archive', 'NodeAndPod') ||
        this.hasParentPermission('viewAnalytics', 'NodeAndPod') ||
        this.hasParentPermission('label', 'Pod');
    },
    hasAnyServicesPermissions() {
      return this.hasParentPermission('list', 'PodService') ||
        this.hasParentPermission('control', 'PodService') ||
        this.hasParentPermission('run', 'FreeService') ||
        this.hasParentPermission('run', 'PaidService');
    },
  },
};
</script>
